import { useCallback, useEffect, useState } from 'react'
import teamSpiritStateService from '../services/teamSpiritStateService'
import { normalizeAttitude } from '../teamSpiritUtils'
import { defaultCountryValues, defaultOpponentCountries } from '../teamSpiritConstants'
import {
  createInitialMatchRows,
  normalizeFixtureCountryKey,
  toDateKey,
} from '../teamSpiritPageUtils'
import {
  normalizeGroupMatchState as mapGroupMatchState,
  buildMatchRowsFromGroup as mapMatchRowsFromGroup,
  getOpponentFromMatch,
} from '../data/teamSpiritGroupMapping'

const useTeamSpiritPageState = (oauthToken, nationalTeamId = 3023) => {
  const activeNationalTeamId = Number(nationalTeamId) || 3023
  const getGroupSelectionId = group => {
    const phase = Number(group?.phase)
    return `${Number(group?.groupId) || 0}:${
      Number.isFinite(phase) && phase > 0 ? phase : 'unresolved'
    }`
  }

  const [nationalCompetition, setNationalCompetition] = useState('CC')
  const [worldCompetition, setWorldCompetition] = useState('WC')
  const [showUpdateDays, setShowUpdateDays] = useState(false)
  const [showPreviousPhase, setShowPreviousPhase] = useState(false)
  const [showPastWeeks, setShowPastWeeks] = useState(false)
  const [, setIsStateHydrated] = useState(false)
  const [loadError, setLoadError] = useState(null)
  const [possessionLeftInput, setPossessionLeftInput] = useState('')
  const [possessionRightInput, setPossessionRightInput] = useState('')
  const [possessionOpponentName, setPossessionOpponentName] = useState('')
  const [groups, setGroups] = useState([])
  const [groupOptions, setGroupOptions] = useState([])
  const [tournaments, setTournaments] = useState([])
  const [activeTournament, setActiveTournament] = useState(null)
  const [selectedTournamentId, setSelectedTournamentId] = useState(null)
  const [selectedGroupId, setSelectedGroupId] = useState(null)
  const [fixtureMatchByDate, setFixtureMatchByDate] = useState({})
  const [fixtureMatchByDateCountry, setFixtureMatchByDateCountry] = useState({})

  const tournamentGroups = selectedTournamentId
    ? groupOptions.filter(
        group => Number(group?.tournamentId) === Number(selectedTournamentId)
      )
    : groupOptions
  const selectedGroup = groups[0] || null

  const selectedGroupTeams = Array.isArray(selectedGroup?.teams)
    ? selectedGroup.teams
    : []
  const activeNationalTeam = selectedGroupTeams.find(
    team => Number(team?.teamId) === activeNationalTeamId
  )
  const activeNationalTeamName = String(activeNationalTeam?.name || '').trim()
  const selectedGroupHasActiveNationalTeam = Boolean(activeNationalTeam)
  const countryGroups = selectedGroupHasActiveNationalTeam
    ? [
        activeNationalTeamName,
        ...selectedGroupTeams
          .filter(team => Number(team?.teamId) !== activeNationalTeamId)
          .map(team => String(team?.name || '').trim())
          .filter(Boolean),
      ]
    : selectedGroupTeams
        .map(team => String(team?.name || '').trim())
        .filter(Boolean)
  const [matchRows, setMatchRows] = useState(() =>
    createInitialMatchRows(defaultOpponentCountries)
  )

  const loadGroups = useCallback(async (selection = {}, isMounted = null) => {
      if (!oauthToken) {
        if (!isMounted || isMounted()) setIsStateHydrated(true)
        return
      }

      try {
        const savedState = await teamSpiritStateService.getGroups(
          oauthToken,
          activeNationalTeamId,
          selection
        )

        if ((isMounted && !isMounted()) || !savedState) return

        const fetchedGroups = Array.isArray(savedState.groups)
          ? savedState.groups
          : []
        setGroups(fetchedGroups)
        const fetchedGroupOptions = Array.isArray(savedState.groupOptions)
          ? savedState.groupOptions
          : []
        setGroupOptions(fetchedGroupOptions)
        const fetchedTournaments = Array.isArray(savedState.tournaments)
          ? savedState.tournaments
          : []
        setTournaments(fetchedTournaments)
        setActiveTournament(savedState.activeTournament || null)
        const nextSelectedTournamentId =
          Number(savedState.selectedTournamentId) ||
          fetchedGroups[0]?.tournamentId ||
          fetchedTournaments[0]?.tournamentId ||
          null
        setSelectedTournamentId(nextSelectedTournamentId)
        setSelectedGroupId(savedState.selectedGroupId || null)
        setNationalCompetition('CC')
        setWorldCompetition('WC')
        setShowUpdateDays(false)
      } catch (error) {
        console.error('Failed to load team spirit groups:', error)
        setLoadError(
          error.response?.data?.error || error.message || 'Failed to load state'
        )
      } finally {
        if (!isMounted || isMounted()) {
          setIsStateHydrated(true)
        }
      }
  }, [oauthToken, activeNationalTeamId])

  useEffect(() => {
    let isMounted = true

    setIsStateHydrated(false)
    loadGroups({}, () => isMounted)

    return () => {
      isMounted = false
    }
  }, [loadGroups])

  useEffect(() => {
    const selectedTeams = Array.isArray(selectedGroup?.teams)
      ? selectedGroup.teams
      : []

    setMatchRows(
      mapMatchRowsFromGroup({
        group: selectedGroup,
        nationalTeamId: activeNationalTeamId,
        createInitialMatchRows,
        normalizeGroupMatchState: match =>
          mapGroupMatchState(match, normalizeAttitude, toDateKey),
        toDateKey,
      })
    )

    const nextFixtureByDate = {}
    const nextFixtureByDateCountry = {}

    selectedTeams.forEach(team => {
      const teamName = String(team?.name || '').trim()
      const teamId = Number(team?.teamId)
      const teamMatches = Array.isArray(team?.matches) ? team.matches : []

      teamMatches.forEach(match => {
        const key = toDateKey(match?.matchDate)
        if (!key) return

        const normalizedMatch = mapGroupMatchState(
          match,
          normalizeAttitude,
          toDateKey
        )
        const matchId = Number(normalizedMatch.matchId)
        const isHome =
          Number.isFinite(teamId) &&
          Number(normalizedMatch.homeTeamId) === Number(teamId)
        const isAway =
          Number.isFinite(teamId) &&
          Number(normalizedMatch.awayTeamId) === Number(teamId)
        const venue = isHome ? 'Home' : isAway ? 'Away' : null
        const opponent = getOpponentFromMatch(normalizedMatch, teamId)

        if (Number.isFinite(matchId)) {
          nextFixtureByDateCountry[
            `${key}::${normalizeFixtureCountryKey(teamName)}`
          ] = {
            matchId,
            venue,
            opponentName: opponent.name,
          }
        }

        if (teamId === Number(activeNationalTeamId)) {
          nextFixtureByDate[key] = {
            matchId: Number.isFinite(matchId) ? matchId : null,
            opponentName: opponent.name,
            venue,
            matchRound: normalizedMatch.matchRound || null,
          }
        }
      })
    })

    setFixtureMatchByDate(nextFixtureByDate)
    setFixtureMatchByDateCountry(nextFixtureByDateCountry)
  }, [selectedGroup, activeNationalTeamId])

  const updateMatchRow = (index, field, value) => {
    setMatchRows(previousRows =>
      previousRows.map((row, rowIndex) =>
        rowIndex === index
          ? {
              ...row,
              [field]: value,
              ...(field === 'attitude' ? { hasManualAttitude: true } : {}),
              ...(field === 'manualSpiritBeforeInput' ? { hasManualSpiritBefore: true } : {}),
            }
          : row
      )
    )
    if (!oauthToken || !['attitude', 'manualSpiritBeforeInput'].includes(field)) return
    const matchId = Number(matchRows[index]?.matchId)
    if (!Number.isFinite(matchId)) return
    teamSpiritStateService.saveManualChange(oauthToken, {
      teamId: activeNationalTeamId,
      matchId,
      editorTeamId: activeNationalTeamId,
      ...(field === 'attitude' ? { attitude: value } : { spiritBefore: value }),
    }).catch(error => {
      console.error('Failed to save Team Spirit manual change:', error)
      setLoadError(error.response?.data?.error || error.message || 'Muutoksen tallennus epäonnistui')
    })
  }

  const updateCountryMatchRow = (index, country, field, value) => {
    setMatchRows(previousRows =>
      previousRows.map((row, rowIndex) => {
        if (rowIndex !== index) return row

        const nextCountryValues = {
          ...(row.countryValues || {}),
          [country]: {
            ...((row.countryValues && row.countryValues[country]) || {
              attitude: 'PIC',
              midfieldInput: '',
              midfieldAnchor: false,
            }),
            [field]: value,
            ...(field === 'attitude' ? { hasManualAttitude: true } : {}),
            ...(field === 'manualSpiritBeforeInput' ? { hasManualSpiritBefore: true } : {}),
          },
        }

        return { ...row, countryValues: nextCountryValues }
      })
    )
    if (!oauthToken || !['attitude', 'manualSpiritBeforeInput'].includes(field)) return
    const teamId = Number(
      selectedGroupTeams.find(team => String(team?.name || '').trim() === country)
        ?.teamId
    )
    const matchId = Number(
      matchRows[index]?.countryValues?.[country]?.matchId
    )
    if (!Number.isFinite(teamId) || !Number.isFinite(matchId)) return
    teamSpiritStateService.saveManualChange(oauthToken, {
      teamId,
      matchId,
      editorTeamId: activeNationalTeamId,
      ...(field === 'attitude' ? { attitude: value } : { spiritBefore: value }),
    }).catch(error => {
      console.error('Failed to save Team Spirit country manual change:', error)
      setLoadError(error.response?.data?.error || error.message || 'Muutoksen tallennus epäonnistui')
    })
  }

  const resetCountryMatchRowManual = (index, country, field = 'spiritBefore') => {
    setMatchRows(previousRows =>
      previousRows.map((row, rowIndex) => {
        if (rowIndex !== index) return row
        const current = row.countryValues?.[country] || {}
        return {
          ...row,
          countryValues: {
            ...(row.countryValues || {}),
            [country]: {
              ...current,
              ...(field === 'attitude'
                ? { attitude: 'PIC', hasManualAttitude: false }
                : { manualSpiritBeforeInput: '', hasManualSpiritBefore: false }),
            },
          },
        }
      })
    )
    const teamId = Number(
      selectedGroupTeams.find(team => String(team?.name || '').trim() === country)
        ?.teamId
    )
    const matchId = Number(matchRows[index]?.countryValues?.[country]?.matchId)
    if (!oauthToken || !Number.isFinite(teamId) || !Number.isFinite(matchId)) return
    teamSpiritStateService.resetManualChange(oauthToken, {
      teamId,
      matchId,
      field,
      editorTeamId: activeNationalTeamId,
    }).catch(error => {
      console.error('Failed to reset Team Spirit country manual change:', error)
      setLoadError(error.response?.data?.error || error.message || 'Muutoksen palautus epäonnistui')
    })
  }

  const resetActiveNationalTeamManual = (index, field) => {
    setMatchRows(previousRows => previousRows.map((row, rowIndex) =>
      rowIndex === index
        ? {
            ...row,
            [field === 'spiritBefore' ? 'manualSpiritBeforeInput' : field]: field === 'attitude' ? 'PIC' : '',
            ...(field === 'attitude' ? { hasManualAttitude: false } : { hasManualSpiritBefore: false }),
          }
        : row
    ))
    const matchId = Number(matchRows[index]?.matchId)
    if (!oauthToken || !Number.isFinite(matchId)) return
    teamSpiritStateService.resetManualChange(oauthToken, {
      teamId: activeNationalTeamId,
      matchId,
      field,
      editorTeamId: activeNationalTeamId,
    }).catch(error => {
      console.error('Failed to reset Team Spirit manual change:', error)
      setLoadError(error.response?.data?.error || error.message || 'Muutoksen palautus epäonnistui')
    })
  }

  const setMidfieldAnchorForCountry = (index, country, checked) => {
    setMatchRows(previousRows =>
      previousRows.map((row, rowIndex) => {
        if (country === activeNationalTeamName) {
          return {
            ...row,
            midfieldAnchor: checked ? rowIndex === index : false,
          }
        }

        const currentCountryValue = {
          ...defaultCountryValues,
          ...((row.countryValues && row.countryValues[country]) || {}),
        }

        return {
          ...row,
          countryValues: {
            ...(row.countryValues || {}),
            [country]: {
              ...currentCountryValue,
              midfieldAnchor: checked ? rowIndex === index : false,
            },
          },
        }
      })
    )
  }

  const selectTournament = tournamentId => {
    const nextTournamentId = Number(tournamentId) || null
    setSelectedTournamentId(nextTournamentId)
    setSelectedGroupId(null)
    loadGroups({ tournamentId: nextTournamentId, includePreviousPhase: showPreviousPhase })
  }

  const selectGroup = selectionId => {
    const nextGroup = tournamentGroups.find(
      group => getGroupSelectionId(group) === String(selectionId)
    )
    if (!nextGroup) return

    setSelectedGroupId(getGroupSelectionId(nextGroup))
    loadGroups({
      tournamentId: selectedTournamentId,
      groupId: nextGroup.groupId,
      phase: nextGroup.phase,
      includePreviousPhase: showPreviousPhase,
    })
  }

  const togglePreviousPhase = checked => {
    setShowPreviousPhase(checked)
    if (!selectedGroup) return
    loadGroups({
      tournamentId: selectedGroup.tournamentId,
      groupId: selectedGroup.groupId,
      phase: selectedGroup.phase,
      includePreviousPhase: checked,
    })
  }

  const reloadSelectedGroup = () => {
    if (!selectedGroup) return Promise.resolve()
    return loadGroups({
      tournamentId: selectedGroup.tournamentId,
      groupId: selectedGroup.groupId,
      phase: selectedGroup.phase,
      includePreviousPhase: showPreviousPhase,
    })
  }

  return {
    group: {
      groups,
      tournaments,
      activeTournament,
      selectedTournamentId,
      tournamentGroups,
      selectedGroupId,
      selectedGroup,
      selectedGroupTeams,
      selectedGroupHasActiveNationalTeam,
      activeNationalTeamId,
      activeNationalTeamName,
      countryGroups,
    },
    fixtures: {
      byDate: fixtureMatchByDate,
      byDateCountry: fixtureMatchByDateCountry,
    },
    rows: {
      matchRows,
    },
    ui: {
      nationalCompetition,
      worldCompetition,
      showUpdateDays,
      showPreviousPhase,
      showPastWeeks,
      loadError,
      possessionLeftInput,
      possessionRightInput,
      possessionOpponentName,
    },
    actions: {
      setSelectedGroupId: selectGroup,
      selectTournament,
      setShowUpdateDays,
      setShowPreviousPhase: togglePreviousPhase,
      reloadSelectedGroup,
      setShowPastWeeks,
      setPossessionLeftInput,
      setPossessionRightInput,
      setPossessionOpponentName,
      updateMatchRow,
      updateCountryMatchRow,
      resetCountryMatchRowManual,
      resetActiveNationalTeamManual,
      setMidfieldAnchorForCountry,
    },
  }
}

export default useTeamSpiritPageState
